<template>
  <div v-if="category">
    <div class="md:container">
      <LexasPageMessage />
    </div>
    <div class="container pb-4 pt-6 md:pt-8 grid gap-6">
      <div class="prose">
        <h1>
          {{ category.name }}
        </h1>
        <div
          :data-collapsed="collapsedDescription"
          class="text-secondary prose data-[collapsed=true]:line-clamp-3"
          @click="collapsedDescription = false"
          v-html="description || ''"
        />
        <button
          v-if="collapsedDescription && (description?.length || 0) > 100"
          class="underline mt-1"
          @click="collapsedDescription = !collapsedDescription"
        >
          Lue lisää
        </button>
      </div>
      <SubCategories
        v-if="category.path"
        :category-path="category.path"
        :sort-alphabetically="true"
      />
    </div>

    <LexasCategoryBanner class="sm:container" />

    <AisInstantSearch
      ref="ais"
      :widgets
      :configuration
      class="scroll-m-24 lg:scroll-m-4 mt-4"
    >
      <div>
        <div class="pb-6 lg:pb-10 flex flex-col gap-6">
          <div class="container">
            <AisStateResults v-slot="{ status, results }" class="font-bold text-secondary mb-4">
              <p v-if="status !== 'idle'">
                Ladataan tuotteita...
              </p>
              <p v-else>
                {{ results?.nbHits }} {{ results?.nbHits === 1 ? 'tuote' : 'tuotetta' }}<span v-if="results?.page">, sivu {{ results?.page + 1 }}</span>
              </p>
            </AisStateResults>

            <div class="flex gap-2 justify-between">
              <ProductFilters class="flex-none" />
              <ProductListSorter />
            </div>
          </div>
          <FilterSelectionList class="lg:container" />
        </div>

        <div class="container">
          <AisHits v-slot="{ items }" ref="ais-hits">
            <div class="product-grid">
              <template v-for="(item, i) in items" :key="item.slug">
                <ProductTile
                  :product="(item as unknown as ProductAlgolia)"
                  class="hover:bg-surface-raised hover:shadow-md transition-[background,_shadow] duration-200"
                  @click="eventHandler(item)"
                />
                <div v-if="i === 8" class="flex items-center justify-center">
                  <LexasProductTileBanner />
                </div>
              </template>
            </div>
          </AisHits>

          <AisPagination v-slot="{ nbHits, currentRefinement, refine }" class="py-10 lg:py-12">
            <BasePagination
              :per-page="hitsPerPage"
              :item-amount="nbHits"
              :current-page="currentRefinement + 1"
              @page-change="(e: number) => { refine(e - 1); scrollToProductsTop() }"
            />
          </AisPagination>
        </div>
      </div>
    </AisInstantSearch>
    <div v-if="containsVehicles" class="mb-8">
      <ld-slot name="tuotevalikko" />
    </div>
  </div>
  <div v-else>
    <h1>404</h1>
    <p>Kategoriaa ei löytynyt</p>
  </div>
</template>

<script setup lang="ts">
import { AisInstantSearch } from '#components'
import { useAlgolia } from '~/composables/useAlgolia'
import { useCategoryStore } from '~/stores/category'
import { useProductFilterStore } from '~/stores/productFilter'
import SubCategories from '~/components/category/subcategories/SubCategories.vue'
import ProductTile from '~/components/category/ProductTile.vue'
import ProductFilters from '~/components/category/product-filters/ProductFilters.vue'
import FilterSelectionList from '~/components/category/product-filters/FilterSelectionList.vue'
import ProductListSorter from '~/components/category/ProductListSorter.vue'
import { attributesForProductTile } from '~/helpers/instantsearch'
import type { ProductFilter } from '~/types/productFilter'
import type { ProductAlgolia } from '~/types/product'
import { COLLECTIONS } from '~/lib/search/typesense'
import { categoryMetaDescription, categoryPageTitle } from '~/helpers/seoText'

const config = useRuntimeConfig()
const route = useRoute()
const algolia = useAlgolia()
const categoryStore = useCategoryStore()
const { getProductFiltersByCategoryPath, getProductSortersInAisFormat } = useProductFilterStore()
const currentPage = computed(() => route.query.sivu ? Number(route.query.sivu) : 1)

const category = categoryStore.getCategoryByPath('category/' + [...route.params.slug].flat().join('/') || '')
const filtersForCategory: ProductFilter[] = getProductFiltersByCategoryPath(route.path)
const valuesToSortBy = getProductSortersInAisFormat

const filters = computed((() => category?.algoliaFilters || `categories.id:'${category?.id}'`))

const containsVehicles = computed(() => category?.path?.startsWith('category/ajoneuvot') || false)

const algoliaClient = useSearchClient()

const hitsPerPage = 23

const widgets = computed(() => [
  useAisHits({
    escapeHTML: true
  }),
  useAisConfigure({
    searchParameters: {
      filters: filters.value,
      distinct: true,
      hitsPerPage,
      attributesToHighlight: [],
      attributesToRetrieve: attributesForProductTile,
      query: '',
      // Typesense curation rules for category listings are tagged "kategoria-<id>"
      ...(category?.id && { ruleContexts: [`kategoria-${category.id}`] })
    }
  }),
  useAisSearchBox({}),
  useAisPagination({}),
  useAisStats({}),
  useAisClearRefinements({}),
  useAisCurrentRefinements({}),
  useAisSortBy({ items: valuesToSortBy }),
  ...algolia.getRefinements(filtersForCategory)
])

const algoliaRouter = useCategoryRouter()
const configuration = ref({
  indexName: COLLECTIONS.products,
  searchClient: algoliaClient,
  routing: algoliaRouter.value,
  stalledSearchDelay: 50,
  future: {
    preserveSharedStateOnUnmount: true
  }
})

// const { data: products } = await useAsyncSearch({ key: 'category_test', indexName: 'veke_products_dev', query: 'Matto' })

const eventHandler = (_item: unknown) => {
  /**
   * Algolia Insights call causes a CORB-warning in Chrome console:
   * https://support.algolia.com/hc/en-us/articles/4418789171601-I-am-seeing-a-Cross-Origin-Read-Blocking-error-CORB-error-in-the-console-when-using-Google-Chrome-what-does-this-mean-
   * https://github.com/algolia/search-insights.js/issues/253#issuecomment-842152675
   */
  // AisHits.value.addResultsToState()
  // $algoliaInsights('clickedObjectIDsAfterSearch', {
  //   userToken: store.getters[UserGetters.loggedIn] ? 'logged_in_user' : 'guest_user',
  //   index: config.ALGOLIA_INDEX,
  //   eventName: 'product_click_in_category_listing',
  //   queryID: item.__queryID,
  //   objectIDs: [item.objectID],
  //   positions: [item.__position]
  // })
}

const ais = ref<InstanceType<typeof AisInstantSearch> | null>(null)

const scrollToProductsTop = () => {
  if (ais.value) {
    ais.value.$el.scrollIntoView({ behavior: 'smooth' })
  }
}

if (!category) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Category Not Found'
  })
}

// ?sivu past the last page was an empty, indexable 200 page (SEO review 22.10.2026). The count query runs only
// for paginated URLs. A failed search also returns 0 hits, so only a known product count can make a page 404.
if (currentPage.value > 1) {
  const { data: productCount } = await useAsyncData(`category-count-${category.id}`, () =>
    useTypesenseSearch().search(COLLECTIONS.products, '', { filters: filters.value, distinct: true, hitsPerPage: 1, attributesToRetrieve: ['sku'] })
      .then(result => result.nbHits))
  if (productCount.value && (currentPage.value - 1) * hitsPerPage >= productCount.value) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
  }
}

// Not part of the category list that comes with every page (stores/category.ts)
const { data: description } = await useAsyncData(`category-description-${category.id}`, () =>
  $fetch<string>('/api/category-description', { query: { id: category.id } }).catch(() => ''))

const collapsedDescription = ref(true)

// Unique titles and a description for every category, at most 155 characters (SEO review 22.10.2026)
const pageTitle = categoryPageTitle(category, categoryStore.categories)
const metaDescription = computed(() => categoryMetaDescription(category, categoryStore.categories, description.value))

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  ogUrl: `${config.public.BASE_URL}/${category.path}`,
  description: () => metaDescription.value,
  ogDescription: () => metaDescription.value
})
useHead(() => ({
  link: [
    {
      rel: 'canonical',
      href: `${config.public.BASE_URL}/${category.path}${currentPage.value > 1 ? `?sivu=${currentPage.value}` : ''}`
    }
  ]
}))
</script>
